'use client';

import { useEffect, useRef } from 'react';
import Script from 'next/script';
import {
  AdyenCheckout as AdyenCheckoutSDK,
  Dropin,
  Card,
  ApplePay,
  GooglePay,
  Bancontact,
  Redirect,
  SepaDirectDebit,
  Twint,
  EPS,
  Multibanco,
  PayPal,
} from '@adyen/adyen-web';
import '@adyen/adyen-web/styles/adyen.css';
import './adyen-checkout-theme.css';
import type {
  CardConfiguration,
  InstantPaymentTypes,
  PaymentCompletedData,
  PaymentFailedData,
  UIElement,
} from '@adyen/adyen-web';
import { buildRedirectDetails } from '@/tenants/_shared/lib/adyenRedirectDetails';
import { prefetchTurnstileToken } from '@/lib/turnstile';

/**
 * Map app locale → Adyen SDK locale.
 * Omitted locales (id, tr) have no Adyen translations and fall back to en-US.
 */
const LOCALE_TO_ADYEN: Record<string, string> = {
  en: 'en-US',
  de: 'de-DE',
  el: 'el-GR',
  es: 'es-ES',
  fr: 'fr-FR',
  hu: 'hu-HU',
  it: 'it-IT',
  ja: 'ja-JP',
  nl: 'nl-NL',
  pl: 'pl-PL',
  pt: 'pt-PT',
  'pt-br': 'pt-BR',
  ro: 'ro-RO',
  ru: 'ru-RU',
};

/**
 * Card/CVC/expiry render inside Adyen-hosted iframes that can't inherit page
 * CSS, so their colors must be passed as JS config and mirror the CSS theme
 * in adyen-checkout-theme.css.
 */
const securedFieldStyles: Record<'dark' | 'light', NonNullable<CardConfiguration['styles']>> = {
  dark: {
    base: {
      color: '#f5f5f4',
      background: '#292524',
    },
    placeholder: {
      color: '#78716c',
    },
    error: {
      color: '#ef4444',
    },
  },
  light: {
    base: {
      color: '#1c1917',
      background: '#ffffff',
    },
    placeholder: {
      color: '#a8a29e',
    },
    error: {
      color: '#dc2626',
    },
  },
};

export interface AdyenCheckoutConfig {
  clientKey: string;
  countryCode: string;
  locale?: string;
  adyenLive?: boolean;
  applePay?: boolean;
  googlePay?: boolean;
  ideal?: boolean;
  bancontact?: boolean;
  sepa?: boolean;
  twint?: boolean;
  eps?: boolean;
  multibanco?: boolean;
  paypal?: boolean;
}

export interface AdyenCheckoutProps {
  session: {
    id: string;
    sessionData: string;
  };
  config: AdyenCheckoutConfig;
  redirectResult?: string;
  onPaymentCompleted: (data: PaymentCompletedData, element?: UIElement) => void;
  onPaymentFailed: (data: PaymentFailedData | undefined, element?: UIElement) => void;
  /** Called with the Adyen payment method type when the donor clicks Pay (before redirect) */
  onBeforeSubmit?: (paymentMethodType: string) => Promise<void>;
  /** Called after the Drop-in mounts successfully */
  onReady?: () => void;
  /** Called when SDK initialization fails */
  onError?: (error: Error) => void;
  /**
   * Visual theme for the secured-field iframes. Must match the surrounding
   * window: 'light' inside [data-theme-light='true'] containers, 'dark'
   * (default) everywhere else — the page CSS keys off the same attribute.
   */
  theme?: 'light' | 'dark';
}

/**
 * Adyen Drop-in checkout shell.
 *
 * Mounts the Adyen Web SDK Drop-in with a pre-created session.
 * Must be loaded client-only — use `dynamic(() => import(...), { ssr: false })` where consumed.
 */
export function AdyenCheckout({
  session,
  config,
  redirectResult,
  onPaymentCompleted,
  onPaymentFailed,
  onBeforeSubmit,
  onReady,
  onError,
  theme = 'dark',
}: AdyenCheckoutProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const dropinRef = useRef<InstanceType<typeof Dropin> | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    let mounted = true;
    // One payment attempt per session at a time. The SDK does not guard submit()
    // (Enter key, double tap, or a Google Pay sheet retry while beforeSubmit is
    // still waiting), and every submit that reaches /payments is a real charge.
    let paymentInFlight = false;

    async function init() {
      try {
        const checkout = await AdyenCheckoutSDK({
          environment: config.adyenLive ? 'live' : 'test',
          clientKey: config.clientKey,
          countryCode: config.countryCode,
          locale: config.locale ? LOCALE_TO_ADYEN[config.locale] : undefined,
          analytics: { enabled: false },
          session: {
            id: session.id,
            sessionData: session.sessionData,
          },
          beforeSubmit: async (state, _component, actions) => {
            // Hold (never settle) a duplicate submit instead of rejecting it: the
            // Google Pay component reports a rejection through onPaymentFailed,
            // which would show "payment failed" while the first attempt succeeds.
            if (paymentInFlight) return;
            paymentInFlight = true;
            const methodType = state.paymentMethod?.type;
            if (methodType && onBeforeSubmit) {
              try {
                await onBeforeSubmit(methodType);
              } catch {
                paymentInFlight = false;
                actions.reject();
                return;
              }
            }
            actions.resolve(state);
          },
          onPaymentCompleted: (data, element) => {
            if (mounted) onPaymentCompleted(data, element);
          },
          onPaymentFailed: (data, element) => {
            paymentInFlight = false;
            if (mounted) onPaymentFailed(data, element);
          },
        });

        if (!mounted || !containerRef.current) return;

        if (redirectResult) {
          checkout.submitDetails(buildRedirectDetails(redirectResult));
          return;
        }

        // Build payment method components based on config flags
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const components: any[] = [Card];
        if (config.applePay) components.push(ApplePay);
        if (config.googlePay) components.push(GooglePay);
        if (config.bancontact) components.push(Bancontact);
        if (config.ideal) components.push(Redirect);
        if (config.sepa) components.push(SepaDirectDebit);
        if (config.twint) components.push(Twint);
        if (config.eps) components.push(EPS);
        if (config.multibanco) components.push(Multibanco);
        if (config.paypal) components.push(PayPal);

        // All wallet methods appear in the regular payment method list (no instant buttons)
        const instantPaymentTypes: InstantPaymentTypes[] = [];

        const dropin = new Dropin(checkout, {
          paymentMethodComponents: components,
          instantPaymentTypes,
          showRadioButton: true,
          paymentMethodsConfiguration: {
            card: {
              hasHolderName: true,
              holderNameRequired: true,
              styles: securedFieldStyles[theme],
            },
            bcmc: {
              hasHolderName: true,
              holderNameRequired: true,
              styles: securedFieldStyles[theme],
            },
            ...(config.applePay && {
              applepay: {
                buttonType: 'donate',
              },
            }),
            ...(config.googlePay && {
              googlepay: {
                buttonType: 'donate',
                buttonSizeMode: 'fill',
                buttonColor: theme === 'light' ? 'black' : 'white',
              },
            }),
          },
        });

        dropin.mount(containerRef.current);
        dropinRef.current = dropin;
        // Any challenge for commitCheckout's token shows now, not behind a wallet sheet.
        prefetchTurnstileToken();
        if (onReady) onReady();
      } catch (err) {
        if (mounted && onError) {
          onError(err instanceof Error ? err : new Error(String(err)));
        }
      }
    }

    init();

    return () => {
      mounted = false;
      dropinRef.current?.unmount();
      dropinRef.current = null;
    };
  }, [
    session.id,
    session.sessionData,
    config.clientKey,
    config.countryCode,
    config.locale,
    config.adyenLive,
    config.applePay,
    config.googlePay,
    config.ideal,
    config.bancontact,
    config.sepa,
    config.twint,
    config.eps,
    config.multibanco,
    config.paypal,
    redirectResult,
    onPaymentCompleted,
    onPaymentFailed,
    onBeforeSubmit,
    onReady,
    onError,
    theme,
  ]);

  return (
    <>
      {config.googlePay && (
        <Script src="https://pay.google.com/gp/p/js/pay.js" strategy="afterInteractive" />
      )}
      <div ref={containerRef} />
    </>
  );
}
